import { useEffect } from "react";
import { useSelector } from "react-redux";

import * as Api from "@/api";
import urls from "@/constants/Urls";
import FormGroup from "@/design/forms/FormGroup";
import InputMedia from "@/design/forms/InputMedia";
import InputSelect from "@/design/forms/InputSelect";
import InputToggle from "@/design/forms/InputToggle";

import { Grid } from "@mui/material";
import PropTypes from "prop-types";

function DeviceSettings({ formHandler, user, specs }) {
  useEffect(() => {
    Promise.resolve(Api.getRingtones());
  }, []);

  const ringtoneStore = useSelector((state) => state.domain.ringtones);

  return (
    <FormGroup title="Device features">
      {(!specs || specs?.ringtone) && (ringtoneStore.list?.length ?? 0) >= 1 && (
        <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
          <InputMedia
            id="ringtoneId"
            label="Ringtone"
            onChange={formHandler}
            value={user.ringtoneId}
            selectValue="id"
            selects={ringtoneStore.list}
            getOptionLabel={(option) => `${option.brand} ${option.ringtone}`}
            getSourceDependency={(option) => option?.token}
            getSourceGeneration={(option) =>
              `${urls.audio}${encodeURIComponent(`ringtone/${option.brand.toLowerCase()}/`)}${option.ringtone}?alt=media&token=${option.token}`
            }
          />
        </Grid>
      )}
      {(!specs || specs?.distinctiveRingtone) && (ringtoneStore.list?.length ?? 0) >= 1 && (
        <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
          <InputMedia
            id="internalRingtoneId"
            label="Internal Ringtone"
            onChange={formHandler}
            value={user.internalRingtoneId}
            selectValue="id"
            selects={ringtoneStore.list}
            getOptionLabel={(option) => `${option.brand} ${option.ringtone}`}
            getSourceDependency={(option) => option?.token}
            getSourceGeneration={(option) =>
              `${urls.audio}${encodeURIComponent(`ringtone/${option.brand.toLowerCase()}/`)}${option.ringtone}?alt=media&token=${option.token}`
            }
          />
        </Grid>
      )}
      {(!specs || specs.accounts === 1) && (
        <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
          <InputSelect
            id="blfTransferMode"
            label="BLF transfer mode"
            onChange={formHandler}
            value={user.blfTransferMode}
            selectValue="value"
            selects={[
              { value: "attendedTransfer", label: "Attended Transfer" },
              { value: "blindTransfer", label: "Blind Transfer" },
              { value: "newCall", label: "New Call" },
            ]}
            getOptionLabel={(option) => option.label}
          />
        </Grid>
      )}
      {(!specs || specs.semiAttendedTransfer) && (
        <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
          <InputToggle
            id="semiAttendedTransfer"
            onChange={formHandler}
            states={[false, true]}
            value={user.semiAttendedTransfer}
            primary="Semi-attended transfer"
            secondary="Allow the user to initiate a semi-attended transfer."
          />
        </Grid>
      )}
      {(!specs || specs.accounts === 1) && (
        <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
          <InputToggle
            id="blfPickupIndication"
            onChange={formHandler}
            states={[false, true]}
            value={user.blfPickupIndication}
            primary="BLF pickup indication"
            secondary="Show on the screen when other devices are being called."
          />
        </Grid>
      )}
      {(!specs || specs.missedCallLog) && (
        <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
          <InputToggle
            id="missedCallLog"
            onChange={formHandler}
            states={[false, true]}
            value={user.missedCallLog}
            primary="Track in call log"
            secondary="Keep track of missed calls in the device's call log."
          />
        </Grid>
      )}
      {(!specs || specs.callWaiting === "yes") && (
        <>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              id="callWaiting"
              onChange={formHandler}
              states={[false, true]}
              value={user.callWaiting}
              primary="Call waiting"
              secondary="Allow this device to receive calls from multiple callers at the same time."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              id="callWaitingTone"
              onChange={formHandler}
              states={[false, true]}
              value={user.callWaitingTone}
              primary="Call waiting tone"
              secondary="Play the call waiting tone when a new incoming call is received during a call."
            />
          </Grid>
        </>
      )}
      {(!specs || specs.allowDND === "yes") && (
        <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
          <InputToggle
            id="allowDnd"
            onChange={formHandler}
            states={[false, true]}
            value={user.allowDnd}
            primary="Allow Do Not Disturb (DND)"
            secondary="Disabling this option when DND is enabled on the device will disable DND, re-enabling this option will restore the DND status."
          />
        </Grid>
      )}
    </FormGroup>
  );
}

DeviceSettings.propTypes = {
  user: PropTypes.object,
  formHandler: PropTypes.func,
  specs: PropTypes.object,
};

export default DeviceSettings;
